/* eslint-disable max-depth */
import { useMemo } from "react";
import { AllSelectedFilterInfo, SelectedFilterInfo } from "web/types/feed-filters";
import {
    PairOfSelectsFilterSection,
    UniversalFilterOption,
    UniversalFiltersData,
} from "web/types/universal-filters";
import { PendingFilters } from "web/react/components/feeds-filters/pending-filters";

/**
 * This is a fun one because the server marks it as always selected regardless
 *  of what the user's done, so we need to figure out if anything's not on the
 *  default value.
 * The assumption is that this is the price selector and the defaults are the
 *  first and last options.
 */
function findPOSSelected(
    section: PairOfSelectsFilterSection,
    pending: PendingFilters
): [SelectedFilterInfo, number] {
    let firstGroup = section.option_groups[0];
    let secondGroup = section.option_groups[1];

    let firstOptions = firstGroup.options;
    let secondOptions = secondGroup.options;

    let defaultFirst = firstOptions[0];
    let defaultSecond = secondOptions[secondOptions.length - 1];

    let first = firstOptions.find((o) => pending.getChecked(o)) || defaultFirst;
    let second = secondOptions.find((o) => pending.getChecked(o)) || defaultSecond;

    let firstNonDefault = first != defaultFirst;
    let secondNonDefault = second != defaultSecond;

    let numSelected = 0;
    if (firstNonDefault || secondNonDefault) {
        numSelected = 1;
    }

    let breadcrumbs: UniversalFilterOption[] = [];

    // We want to show the selected range rather than each select separately, so fake it
    if (firstNonDefault || secondNonDefault) {
        breadcrumbs.push({
            name: "__selectLabel",
            // TODO: i18n
            label: `${first.label} - ${second.label}`,
            // Prevent this showing up in the breadcrumb pill section
            disabled: true,
        } as any);
    }
    // We want to have any changed price filters to show up in the pills
    // area, but they're kinda funky
    if (firstNonDefault) {
        breadcrumbs.push({
            ...first,
            // TODO: i18n
            label: `${firstGroup.title}: ${first.label}`,
        });
    }
    if (secondNonDefault) {
        breadcrumbs.push({
            ...second,
            // TODO: i18n
            label: `${secondGroup.title}: ${second.label}`,
        });
    }
    return [
        {
            breadcrumbs,
            counts: {},
        },
        numSelected,
    ];
}

function findNestedSelections(
    filters: UniversalFilterOption[],
    pending: PendingFilters,
    _data?: SelectedFilterInfo
): [SelectedFilterInfo, number] {
    if (!_data) {
        _data = {
            breadcrumbs: [],
            counts: {},
        };
    }
    let numAdded = 0;

    for (let filter of filters) {
        if (pending.getChecked(filter)) {
            if (filter.is_default) {
                continue;
            }
            _data.breadcrumbs.push(filter);
            numAdded++;
            if (filter.type == "category") {
                let [, num] = findNestedSelections(filter.children, pending, _data);
                numAdded += num;
                if (num > 0) {
                    _data.counts[`${filter.name}=${filter.value}`] = num;
                }
            }
        }
    }

    return [_data, numAdded];
}

export function useSelectedFilterInfo(
    displayedFilters: UniversalFiltersData,
    pendingFilterInfo: PendingFilters
): [number, AllSelectedFilterInfo] {
    return useMemo(() => {
        let numSelected = 0;
        let filterInfo: AllSelectedFilterInfo = {};
        let selected: SelectedFilterInfo;
        let count: number;
        for (let section of displayedFilters.filter_section_list) {
            if (section.type == "pair_of_selects") {
                [selected, count] = findPOSSelected(section, pendingFilterInfo);
            } else {
                [selected, count] = findNestedSelections(
                    section.option_groups[0].options,
                    pendingFilterInfo
                );
            }
            numSelected += count;
            filterInfo[section.id] = selected;
        }

        return [numSelected, filterInfo];
    }, [displayedFilters, pendingFilterInfo]);
}
